import React from "react";
import { useI18n } from "../i18n";
import useSeo from "../hooks/useSeo";
import { Activity, Target, ShieldAlert, Sparkles, Layers, Workflow, Send, ShieldCheck, MonitorSmartphone, Zap, Filter, TrendingUp, CandlestickChart, Clock, Lock, Check, X, Copy, Repeat, Waves, Radar, DoorOpen } from "lucide-react";

const ICONS = { Activity, Target, ShieldAlert, Sparkles, Layers, Workflow, Send, ShieldCheck, MonitorSmartphone, Zap, Filter, TrendingUp, CandlestickChart, Clock, Lock, Copy, Repeat, Waves, Radar, DoorOpen };

export default function Features() {
  const { t, lang } = useI18n();
  const list = t("features.list") || [];
  const rows = t("features.compare_rows") || [];
  useSeo({
    title: lang === "fr"
      ? "Fonctionnalités — Bot de Trading MT5 Dr Trade Scalper Pro"
      : "Features — Dr Trade Scalper Pro MT5 Trading Bot",
    description: lang === "fr"
      ? "Découvrez toutes les fonctionnalités de Dr Trade Scalper Pro : 6 stratégies (STOKY, YAO, MCB, CRT, LOKI, THF Pulse), filtre multi-timeframe, Smart SL, notifications Telegram, gestion du risque, licence par compte MT5."
      : "Discover all Dr Trade Scalper Pro features: 6 strategies (STOKY, YAO, MCB, CRT, LOKI, THF Pulse), multi-timeframe filter, Smart SL, Telegram notifications, risk management, MT5 account licensing.",
    keywords: lang === "fr"
      ? "fonctionnalités EA MT5, Smart SL, filtre MTF, stratégie STOKY, stratégie YAO, stratégie MCB, stratégie CRT, stratégie LOKI, THF Pulse, Expert Advisor scalping"
      : "MT5 EA features, Smart SL, MTF filter, STOKY strategy, YAO strategy, MCB strategy, CRT strategy, LOKI strategy, THF Pulse, scalping Expert Advisor",
    path: "/features",
  });
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="page-features">
      <div className="max-w-2xl mb-12">
        <div className="section-overline mb-3">{t("features.overline")}</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-4">{t("features.title")}</h1>
        <p className="text-zinc-400 text-base">{t("features.sub")}</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((f, i) => {
          const Icon = ICONS[f.i] || Activity;
          return (
            <div key={i} className="glass rounded-2xl p-6 hover:border-cyan-400/40 transition-colors" data-testid={`features-card-${i}`}>
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg bg-cyan-400/10 grid place-items-center shrink-0">
                  <Icon className="h-5 w-5 text-cyan" />
                </div>
                <div>
                  <div className="text-base font-semibold mb-1.5">{f.t}</div>
                  <div className="text-sm text-zinc-400 leading-relaxed">{f.d}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      <section className="mt-24" data-testid="features-compare">
        <div className="max-w-2xl mb-10">
          <div className="section-overline mb-3">{t("features.compare_overline")}</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tighter mb-3">
            {t("features.compare_title")}
          </h2>
          <p className="text-zinc-400 text-base">{t("features.compare_sub")}</p>
        </div>

        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-zinc-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-4 text-left w-44 sm:w-56">&nbsp;</th>
                  <th className="px-5 py-4 text-left">
                    <span className="inline-flex items-center gap-2 text-zinc-400">
                      <X className="h-3.5 w-3.5 text-red-400" /> {t("features.compare_col_a")}
                    </span>
                  </th>
                  <th className="px-5 py-4 text-left">
                    <span className="inline-flex items-center gap-2 text-cyan-300">
                      <Check className="h-3.5 w-3.5" /> {t("features.compare_col_b")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-white/[0.04]" data-testid={`compare-row-${i}`}>
                    <td className="px-5 py-4 font-semibold text-zinc-200">{r.topic}</td>
                    <td className="px-5 py-4 text-zinc-400">
                      <span className="inline-flex items-start gap-2">
                        <X className="h-4 w-4 text-red-400/70 shrink-0 mt-0.5" />
                        <span>{r.a}</span>
                      </span>
                    </td>
                    <td className="px-5 py-4 text-zinc-200">
                      <span className="inline-flex items-start gap-2">
                        <Check className="h-4 w-4 text-cyan-400 shrink-0 mt-0.5" />
                        <span>{r.b}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
